import { User } from "./user";

/* User scopes
EXPERT e
ADMIN a
SALARY BILLING HR b
CUSTOMER SERVICE s
FINANCE f
ACCOUNTING t
AUDITOR r */

export const ADMIN = "a";
export const SALARY_BILLING_HR = "b";
export const CUSTOMER_SERVICE = "s";
export const FINANCE = "f";
export const ACCOUNTING = "t";
export const AUDITOR = "r";
export const EXPERT = "e";

export type AuthScope =
  | typeof ADMIN
  | typeof SALARY_BILLING_HR
  | typeof CUSTOMER_SERVICE
  | typeof FINANCE
  | typeof ACCOUNTING
  | typeof AUDITOR
  | typeof EXPERT;

export const expertNonAdminRoles = [
  { id: SALARY_BILLING_HR, name: "SalaryBillingHr" },
  { id: CUSTOMER_SERVICE, name: "CustomerService" },
  { id: FINANCE, name: "Finance" },
  { id: ACCOUNTING, name: "Accounting" },
  { id: AUDITOR, name: "Auditor" },
];

export const expertRoles = [
  { id: ADMIN, name: "Admin" },
  ...expertNonAdminRoles,
];

export type AccessRightChecks = (
  | ((user: User, eezyCompanyId?: string) => boolean)
  | boolean
)[];

// special eezy companies
const SONIRE = "GRYC0zHXP1GvYSh6UInwKAyg";
const WORKCONTROL_OY = "GRe4xS-GjVlOaSlhIsTH01SQ";

const isSonireOrWorkControl = (eezyCompanyId: string) =>
  eezyCompanyId === SONIRE || eezyCompanyId === WORKCONTROL_OY;

export const isEezyExpert = (user: User) =>
  user?.eezyCompanyIds.some((id) => id.startsWith("GR"));

export const isEezyGroupCompany = (eezyCompanyId: string) =>
  !!eezyCompanyId && eezyCompanyId.startsWith("GR");

const isMyCompany = (user: User, eezyCompanyId: string) => {
  return user.eezyCompanyIds.includes(eezyCompanyId);
};

const isMyCompanyExtended = (user: User, eezyCompanyId: string) =>
  isMyCompany(user, eezyCompanyId) || isSonireOrWorkControl(eezyCompanyId);

export const isExpertAdmin = (user: User) => {
  return user.scope.includes(ADMIN);
};

export const isExpertSalaryBillingHr = (user: User) => {
  return user.scope.includes(SALARY_BILLING_HR);
};

export const isExpertCustomerService = (user: User) => {
  return user.scope.includes(CUSTOMER_SERVICE);
};

export const isExpertCustomerServiceOrAdmin = (user: User | null) => {
  if (!user) return false;
  return isExpertCustomerService(user) || isExpertAdmin(user);
};

export const includesAuthScope = (user: User, authScopes: AuthScope[]) =>
  authScopes.some((role) => user.scope.includes(role));

export const canExpertUseMessaging = (user: User | null) => {
  if (!user) return false;

  // Temporary: admins during development
  if (isExpertAdmin(user)) return true;

  // Aspa — SUPPORT conversations
  if (isExpertCustomerService(user)) return true;

  const notAllowed: AuthScope[] = [
    SALARY_BILLING_HR,
    FINANCE,
    ACCOUNTING,
    AUDITOR,
  ];

  return user.scope.includes(EXPERT) && !includesAuthScope(user, notAllowed);
};

export const canExpertEditAccumulations = (user: User) =>
  isExpertSalaryBillingHr(user) || isExpertAdmin(user);

/* PAYSLIPS AND INVOICES */

export const canExpertChangeToNonbillable = (user: User) => {
  return isExpertSalaryBillingHr(user) || isExpertAdmin(user);
};

export const canExpertReadInvoices = (user: User) => {
  if (includesAuthScope(user, [CUSTOMER_SERVICE])) return false;

  // rest of the roles can read some invoices, which backend filters based on access rights
  return true;
};

export const canExpertModifyPayslips = (user: User) => {
  return user.scope.includes(ADMIN) || user.scope.includes(SALARY_BILLING_HR);
};

export const canExpertDeletePayslips = (user: User) => {
  return user.scope.includes(ADMIN) || user.scope.includes(SALARY_BILLING_HR);
};

export const canExpertModifyInvoices = (user: User) => {
  return (
    user.scope.includes(ADMIN) ||
    user.scope.includes(SALARY_BILLING_HR) ||
    user.scope.includes(ACCOUNTING)
  );
};

export const canExpertExamineInvoice = (user: User) => {
  return user.scope.includes(ADMIN) || user.scope.includes(SALARY_BILLING_HR);
};

/* SETTINGS */

export const canExpertAccessSettings = (user: User) => {
  return (
    user.scope.includes(ADMIN) ||
    user.scope.includes(SALARY_BILLING_HR) ||
    user.scope.includes(FINANCE) ||
    user.scope.includes(ACCOUNTING)
  );
};

/* ORDERS */

export const canExpertPlaceAndEditOrders = (user: User) => {
  const noAccess: AuthScope[] = [FINANCE, ACCOUNTING, AUDITOR];
  if (includesAuthScope(user, noAccess)) return false;

  return (
    user.scope.includes(ADMIN) ||
    user.scope.includes(EXPERT) ||
    user.scope.includes(SALARY_BILLING_HR) ||
    user.scope.includes(CUSTOMER_SERVICE)
  );
};

export const canExpertReadOrder = (user: User, eezyCompanyId: string) => {
  if (includesAuthScope(user, [AUDITOR])) {
    return isEezyExpert(user)
      ? isEezyGroupCompany(eezyCompanyId)
      : isMyCompany(user, eezyCompanyId);
  }

  return true;
};

/* TALENT PROFILE */

export const canExpertEditTalentProfile = (user: User) => {
  if (
    user.scope.includes(FINANCE) ||
    user.scope.includes(ACCOUNTING) ||
    user.scope.includes(AUDITOR)
  ) {
    return false;
  }

  return true;
};

export const canExpertEditTalentSalaryInfo = (user: User) => {
  return user.scope.includes(ADMIN) || user.scope.includes(SALARY_BILLING_HR);
};

export const canExpertEditTalentTaxNumber = (user: User) => {
  return (
    user.scope.includes(ADMIN) ||
    user.scope.includes(SALARY_BILLING_HR) ||
    user.scope.includes(EXPERT)
  );
};

export const canExpertEditManualSalaries = (user: User) => {
  return user.scope.includes(ADMIN) || user.scope.includes(SALARY_BILLING_HR);
};

export const canExpertEditWithholdingTaxCorrection = (user: User) => {
  return user.scope.includes(ADMIN) || user.scope.includes(SALARY_BILLING_HR);
};

export const canExpertEditEmployment = (user: User, eezyCompanyId: string) => {
  if (user.scope.includes(ADMIN) || user.scope.includes(SALARY_BILLING_HR))
    return true;

  const noAccess: AuthScope[] = [
    CUSTOMER_SERVICE,
    FINANCE,
    ACCOUNTING,
    AUDITOR,
  ];
  if (includesAuthScope(user, noAccess)) return false;

  if (includesAuthScope(user, [EXPERT])) {
    return isEezyExpert(user)
      ? isEezyGroupCompany(eezyCompanyId as string)
      : isMyCompanyExtended(user, eezyCompanyId as string);
  }

  return false;
};

export const canExpertEditAssignment = (user: User, eezyCompanyId: string) => {
  if (
    user.scope.includes(ADMIN) ||
    user.scope.includes(SALARY_BILLING_HR) ||
    user.scope.includes(CUSTOMER_SERVICE)
  )
    return true;

  if (includesAuthScope(user, [EXPERT])) {
    // TODO: refactor this to function companyBelogsToMyGroup
    return isEezyExpert(user)
      ? isEezyGroupCompany(eezyCompanyId)
      : isMyCompanyExtended(user, eezyCompanyId);
  }

  return false;
};
export const canExpertReadAssigmentPricingInfo = (
  user: User,
  eezyCompanyId: string,
) => {
  if (
    user.scope.includes(ADMIN) ||
    user.scope.includes(SALARY_BILLING_HR) ||
    user.scope.includes(CUSTOMER_SERVICE) ||
    user.scope.includes(FINANCE) ||
    user.scope.includes(ACCOUNTING)
  )
    return true;

  if (includesAuthScope(user, [EXPERT])) {
    return isEezyExpert(user)
      ? isEezyGroupCompany(eezyCompanyId)
      : isMyCompanyExtended(user, eezyCompanyId);
  }

  if (includesAuthScope(user, [AUDITOR])) {
    return isEezyExpert(user)
      ? isEezyGroupCompany(eezyCompanyId)
      : isMyCompany(user, eezyCompanyId);
  }

  return false;
};

export const canExpertReadUnionPayment = (user: User) => {
  return (
    user.scope.includes(ADMIN) ||
    user.scope.includes(SALARY_BILLING_HR) ||
    user.scope.includes(FINANCE) ||
    user.scope.includes(ACCOUNTING) ||
    user.scope.includes(AUDITOR)
  );
};

export const canExpertEditUnionPayment = (user: User) => {
  return user.scope.includes(ADMIN) || user.scope.includes(SALARY_BILLING_HR);
};

/* CUSTOMER VIEW */

export const canExpertAddCustomerCompany = (user: User) => {
  if (user.scope.includes(ADMIN)) return true;
  return false;
};

export const canExpertEditCustomer = (user: User) => {
  if (
    user.scope.includes(FINANCE) ||
    user.scope.includes(ACCOUNTING) ||
    user.scope.includes(AUDITOR)
  ) {
    return false;
  }

  return true;
};

export const canExpertAddCustomerOrganization = (user: User) => {
  if (user.scope.includes(ADMIN)) return true;
  return false;
};

export const canExpertEditOrderTemplate = (
  user: User,
  eezyCompanyId: string,
) => {
  if (
    user.scope.includes(ADMIN) ||
    user.scope.includes(SALARY_BILLING_HR) ||
    user.scope.includes(CUSTOMER_SERVICE)
  )
    return true;

  if (includesAuthScope(user, [EXPERT])) {
    return isEezyExpert(user)
      ? isEezyGroupCompany(eezyCompanyId)
      : isMyCompanyExtended(user, eezyCompanyId);
  }
};

export const canExpertAddContract = (user: User) => {
  if (user.scope.includes(ADMIN)) return true;
  return false;
};

export const canExpertEditContract = (user: User, eezyCompanyId: string) => {
  if (
    user.scope.includes(ADMIN) ||
    user.scope.includes(SALARY_BILLING_HR) ||
    user.scope.includes(CUSTOMER_SERVICE)
  )
    return true;

  if (includesAuthScope(user, [EXPERT])) {
    return isEezyExpert(user)
      ? isEezyGroupCompany(eezyCompanyId)
      : isMyCompanyExtended(user, eezyCompanyId);
  }
};

export const canExpertEditContractValidFrom = (
  user: User,
  eezyCompanyId: string,
) => {
  if (user.scope.includes(ADMIN)) return true;
  return false;
};

export const canExpertReadContract = (user: User, eezyCompanyId: string) => {
  if (
    user.scope.includes(ADMIN) ||
    user.scope.includes(SALARY_BILLING_HR) ||
    user.scope.includes(CUSTOMER_SERVICE) ||
    user.scope.includes(FINANCE) ||
    user.scope.includes(ACCOUNTING)
  )
    return true;

  if (includesAuthScope(user, [EXPERT])) {
    return isEezyExpert(user)
      ? isEezyGroupCompany(eezyCompanyId)
      : isMyCompanyExtended(user, eezyCompanyId);
  }

  if (includesAuthScope(user, [AUDITOR])) {
    return isEezyExpert(user)
      ? isEezyGroupCompany(eezyCompanyId)
      : isMyCompany(user, eezyCompanyId);
  }
};

// TODO For YLPE only SALARY_BILLING_HR, CUSTOMER_SERVICE and ADMIN can edit
export const canExpertEditEntries = (user: User) => {
  return (
    user.scope.includes(ADMIN) ||
    user.scope.includes(EXPERT) ||
    user.scope.includes(SALARY_BILLING_HR) ||
    user.scope.includes(CUSTOMER_SERVICE)
  );
};

// TODO For YLPE only SALARY_BILLING_HR and ADMIN can approve
export const canExpertApproveEntries = (user: User) => {
  if (user.scope.includes(CUSTOMER_SERVICE)) {
    return false;
  }
  return (
    user.scope.includes(ADMIN) ||
    user.scope.includes(EXPERT) ||
    user.scope.includes(SALARY_BILLING_HR)
  );
};

export const canExpertReadEmployment = (user: User, eezyCompanyId: string) => {
  return isEezyExpert(user)
    ? canEezyExpertReadEmployment(user, eezyCompanyId)
    : canFranchiseExpertReadEmployment(user, eezyCompanyId);
};

export const canEezyExpertReadEmployment = (
  user: User,
  eezyCompanyId: string,
) => {
  const canReadAll: AuthScope[] = [
    SALARY_BILLING_HR,
    CUSTOMER_SERVICE,
    FINANCE,
    ACCOUNTING,
    ADMIN,
  ];
  if (includesAuthScope(user, canReadAll)) {
    return true;
  }

  if (includesAuthScope(user, [EXPERT, AUDITOR])) {
    // eezy expert or auditor can view employments from eezy group companies
    return isEezyGroupCompany(eezyCompanyId);
  }

  // other eezy expert roles can view all employments
  return true;
};

export const canFranchiseExpertReadEmployment = (
  user: User,
  eezyCompanyId: string,
) => {
  if (includesAuthScope(user, [EXPERT])) {
    // franchise expert can view employments from own eezy group + sonire + workcontrol
    return isMyCompanyExtended(user, eezyCompanyId);
  }

  if (includesAuthScope(user, [AUDITOR])) {
    // franchise auditor can view employments only from own eezy group
    return isMyCompany(user, eezyCompanyId);
  }

  // franchise experts can't have roles other than expert or auditor?
  return false;
};

export const canExpertWriteJobAd = (user: User) => {
  const noAccess: AuthScope[] = [FINANCE, ACCOUNTING, AUDITOR];
  if (includesAuthScope(user, noAccess)) return false;

  return (
    user.scope.includes(ADMIN) ||
    user.scope.includes(EXPERT) ||
    user.scope.includes(SALARY_BILLING_HR) ||
    user.scope.includes(CUSTOMER_SERVICE)
  );
};
